import { SegmentationFeatureState } from '../../../@types/state-types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../@types/types'

export type IdentityChip = SegmentationType
export type IntentChip = TransactionType | 'RENT_LIMITED'
export type PropertyTypeChip = RealEstateCategory

export const AVAILABLE_INTENTS_FOR_IDENTITY: Record<IdentityChip, IntentChip[]> = {
  [SegmentationType.PROPERTY_OWNER]: [TransactionType.RENT, TransactionType.SELL, 'RENT_LIMITED'],
  [SegmentationType.AGENT]: [TransactionType.RENT, TransactionType.SELL, 'RENT_LIMITED'],
  [SegmentationType.TENANT]: [TransactionType.RENT, TransactionType.SELL, 'RENT_LIMITED'],
}

export const AVAILABLE_PROPERTY_TYPES: Record<IdentityChip, Partial<Record<IntentChip, PropertyTypeChip[]>>> = {
  [SegmentationType.PROPERTY_OWNER]: {
    [TransactionType.RENT]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.GARAGE,
      RealEstateCategory.OFFICE,
      RealEstateCategory.PRODUCTION_HALL,
      RealEstateCategory.STORE,
      RealEstateCategory.GASTRONOMY,
      RealEstateCategory.INDUSTRIAL_PLOT,
    ],
    [TransactionType.SELL]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.GARAGE,
      RealEstateCategory.OFFICE,
      RealEstateCategory.PRODUCTION_HALL,
      RealEstateCategory.STORE,
      RealEstateCategory.GASTRONOMY,
      RealEstateCategory.INDUSTRIAL_PLOT,
    ],
    RENT_LIMITED: [RealEstateCategory.APARTMENT, RealEstateCategory.HOUSE, RealEstateCategory.FLAT_SHARE_ROOM],
  },
  [SegmentationType.AGENT]: {
    [TransactionType.RENT]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.GARAGE,
      RealEstateCategory.OFFICE,
      RealEstateCategory.PRODUCTION_HALL,
      RealEstateCategory.STORE,
      RealEstateCategory.GASTRONOMY,
      RealEstateCategory.INDUSTRIAL_PLOT,
    ],
    [TransactionType.SELL]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.GARAGE,
      RealEstateCategory.OFFICE,
      RealEstateCategory.PRODUCTION_HALL,
      RealEstateCategory.STORE,
      RealEstateCategory.GASTRONOMY,
      RealEstateCategory.INDUSTRIAL_PLOT,
    ],
    RENT_LIMITED: [RealEstateCategory.APARTMENT, RealEstateCategory.HOUSE, RealEstateCategory.FLAT_SHARE_ROOM],
  },
  [SegmentationType.TENANT]: {
    [TransactionType.RENT]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.GARAGE,
      RealEstateCategory.OFFICE,
      RealEstateCategory.PRODUCTION_HALL,
      RealEstateCategory.STORE,
      RealEstateCategory.GASTRONOMY,
      RealEstateCategory.INDUSTRIAL_PLOT,
    ],
    [TransactionType.SELL]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.GARAGE,
      RealEstateCategory.OFFICE,
      RealEstateCategory.PRODUCTION_HALL,
      RealEstateCategory.STORE,
      RealEstateCategory.GASTRONOMY,
      RealEstateCategory.INDUSTRIAL_PLOT,
    ],
    RENT_LIMITED: [RealEstateCategory.APARTMENT, RealEstateCategory.HOUSE, RealEstateCategory.FLAT_SHARE_ROOM],
  },
}

export const getAvailableIntents = (identity: IdentityChip | null): IntentChip[] => {
  if (!identity) return []
  return AVAILABLE_INTENTS_FOR_IDENTITY[identity]
}

export const getAvailablePropertyTypes = (
  identity: IdentityChip | null,
  intent: IntentChip | null
): PropertyTypeChip[] => {
  if (!identity || !intent) return []
  return AVAILABLE_PROPERTY_TYPES[identity]?.[intent] || []
}

export const SEGMENTATION_TREE: Record<PropertyTypeChip, string[]> = {
  [RealEstateCategory.APARTMENT]: [],
  [RealEstateCategory.HOUSE]: [],
  [RealEstateCategory.GARAGE]: [],
  [RealEstateCategory.OFFICE]: [],
  [RealEstateCategory.STORE]: [],
  [RealEstateCategory.GASTRONOMY]: [],
  [RealEstateCategory.PRODUCTION_HALL]: [],
  [RealEstateCategory.INDUSTRIAL_PLOT]: [],
  [RealEstateCategory.FLAT_SHARE_ROOM]: [],
  [RealEstateCategory.SHORT_TERM_ACCOMMODATION]: [],
  [RealEstateCategory.PLOT]: [],
  [RealEstateCategory.INVESTMENT]: [],
  [RealEstateCategory.HANGAR]: [],
  [RealEstateCategory.SPECIAL_INDUSTRY]: [],
}

const computeVisibleFields = (selectedPropertyTypes: PropertyTypeChip[]): Set<string> => {
  const visibleFields = new Set<string>()

  selectedPropertyTypes.forEach((propertyType) => {
    const fields = SEGMENTATION_TREE[propertyType]
    fields.forEach((field) => visibleFields.add(field))
  })

  return visibleFields
}

// Business property type categories
const BUSINESS_CATEGORIES = [
  RealEstateCategory.SPECIAL_INDUSTRY,
  RealEstateCategory.OFFICE,
  RealEstateCategory.GASTRONOMY,
  RealEstateCategory.HANGAR,
  RealEstateCategory.STORE,
  RealEstateCategory.INDUSTRIAL_PLOT,
]

const isBusinessCategory = (propertyType: PropertyTypeChip): boolean => {
  return BUSINESS_CATEGORIES.includes(propertyType)
}

export const initialState: SegmentationFeatureState = {
  selectedIdentity: null,
  selectedIntent: null,
  selectedPropertyTypes: [],
  visibleFields: new Set<string>(),
  isShortTermAccommodation: false,
  showBusinessCategories: false,
  validationErrors: {},
}

const segmentationFeatureSlice = createSlice({
  name: 'segmentationFeature',
  initialState,
  reducers: {
    selectIdentityChip(state, action: PayloadAction<{ identity: IdentityChip }>) {
      state.selectedIdentity = action.payload.identity
      state.visibleFields = computeVisibleFields(state.selectedPropertyTypes)
      state.validationErrors = {}
    },
    deselectIdentityChip(state) {
      state.selectedIdentity = null
      state.visibleFields = computeVisibleFields(state.selectedPropertyTypes)
      state.validationErrors = {}
    },
    selectIntentChip(state, action: PayloadAction<{ intent: IntentChip }>) {
      state.selectedIntent = action.payload.intent
      state.isShortTermAccommodation = false
      state.visibleFields = computeVisibleFields(state.selectedPropertyTypes)
      state.validationErrors = {}
    },
    deselectIntentChip(state) {
      state.selectedIntent = null
      state.isShortTermAccommodation = false
      state.visibleFields = computeVisibleFields(state.selectedPropertyTypes)
      state.validationErrors = {}
    },
    selectShortTermAccommodation(state) {
      state.selectedIntent = null
      state.isShortTermAccommodation = true
      state.selectedPropertyTypes = []
      state.validationErrors = {}
    },
    deselectShortTermAccommodation(state) {
      state.isShortTermAccommodation = false
      state.validationErrors = {}
    },
    showBusinessCategories(state) {
      state.showBusinessCategories = true
      state.selectedPropertyTypes = []
      state.validationErrors = {}
    },
    hideBusinessCategories(state) {
      state.showBusinessCategories = false
      state.validationErrors = {}
    },
    selectPropertyTypeChip(state, action: PayloadAction<{ propertyType: PropertyTypeChip }>) {
      const { propertyType } = action.payload
      // For single-select: replace the array
      state.selectedPropertyTypes = [propertyType]
      // Keep business categories visible if selecting a business category, otherwise hide them
      if (!isBusinessCategory(propertyType)) {
        state.showBusinessCategories = false
      }
      state.visibleFields = computeVisibleFields(state.selectedPropertyTypes)
      state.validationErrors = {}
    },
    deselectPropertyTypeChip(state, action: PayloadAction<{ propertyType: PropertyTypeChip }>) {
      const { propertyType } = action.payload
      state.selectedPropertyTypes = state.selectedPropertyTypes.filter(
        (type: PropertyTypeChip) => type !== propertyType
      )
      // Keep business categories visible if deselecting a business category, otherwise hide them
      if (!isBusinessCategory(propertyType)) {
        state.showBusinessCategories = false
      }
      state.visibleFields = computeVisibleFields(state.selectedPropertyTypes)
      state.validationErrors = {}
    },
    togglePropertyTypeChip(state, action: PayloadAction<{ propertyType: PropertyTypeChip }>) {
      const { propertyType } = action.payload
      if (state.selectedPropertyTypes.includes(propertyType)) {
        state.selectedPropertyTypes = state.selectedPropertyTypes.filter(
          (type: PropertyTypeChip) => type !== propertyType
        )
      } else {
        state.selectedPropertyTypes.push(propertyType)
      }
      state.visibleFields = computeVisibleFields(state.selectedPropertyTypes)
      state.validationErrors = {}
    },
    setValidationErrors(
      state,
      action: PayloadAction<{ identity?: string[]; intent?: string[]; propertyTypes?: string[] }>
    ) {
      state.validationErrors = action.payload
    },
    resetSegmentation(state) {
      state.selectedIdentity = initialState.selectedIdentity
      state.selectedIntent = initialState.selectedIntent
      state.selectedPropertyTypes = []
      state.visibleFields = new Set<string>()
      state.isShortTermAccommodation = false
      state.showBusinessCategories = false
      state.validationErrors = {}
    },
  },
})

export const {
  selectIdentityChip,
  deselectIdentityChip,
  selectIntentChip,
  deselectIntentChip,
  selectShortTermAccommodation,
  deselectShortTermAccommodation,
  showBusinessCategories,
  hideBusinessCategories,
  selectPropertyTypeChip,
  deselectPropertyTypeChip,
  togglePropertyTypeChip,
  setValidationErrors,
  resetSegmentation,
} = segmentationFeatureSlice.actions
export default segmentationFeatureSlice.reducer
